import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, CalendarRange } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Input } from "@/components/ui/input";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import {
    dkMessage, fmt, fmtPct, fmtQuantity, fmtSigned, isIsoDate, isIsoMonth, monthsBack, pctOf, unitOf,
} from "./dkUtils";
import { ExportButton, MonthPicker, NoteBox, OfflineBox, cardClass, chipClass } from "./widgets";

const V = OPS.dk.variance;

/**
 * «الفروقات»: the cross-branch board that replaces the sheet's «الفرق» —
 * locations down × items across, for a month or a date range. A cell is the
 * unexplained variance (after W&M waste + meals) and its % of the theoretical,
 * red when flagged; «ناقص» counts the days without a closing. Kilos and pieces
 * are never added together (one item per column). A cell opens that location's
 * month in «الشهر».
 */
export function VarianceBoard({ scope, canOpenMonth }) {
    const { t, tn, lang } = useI18n();
    const [searchParams, setSearchParams] = useSearchParams();
    const today = scope?.today || "";
    const thisMonth = today.slice(0, 7);
    const months = useMemo(() => monthsBack(thisMonth, 12), [thisMonth]);
    const paramMonth = searchParams.get("month");
    const month = isIsoMonth(paramMonth) && months.includes(paramMonth) ? paramMonth : thisMonth;

    const [rangeMode, setRangeMode] = useState("month");
    const [from, setFrom] = useState(thisMonth ? `${thisMonth}-01` : "");
    const [to, setTo] = useState(today);
    const rangeOk = isIsoDate(from) && isIsoDate(to) && from <= to && (!today || to <= today);

    const setMonth = (v) => {
        const next = new URLSearchParams(searchParams);
        next.set("month", v);
        setSearchParams(next, { replace: true });
    };

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const seq = useRef(0);
    const params = rangeMode === "month" ? { month } : (rangeOk ? { date_from: from, date_to: to } : null);
    const paramsKey = JSON.stringify(params);

    const load = useCallback(async () => {
        const p = JSON.parse(paramsKey);
        if (!p) { setLoading(false); return; }
        const my = ++seq.current;
        setLoading(true);
        setError(null);
        try {
            const r = await api.get("/dk/variance", { params: p });
            if (my === seq.current) setData({ key: paramsKey, data: r.data });
        } catch (e) {
            if (my === seq.current) setError(e);
        } finally {
            if (my === seq.current) setLoading(false);
        }
    }, [paramsKey]);
    useEffect(() => { load(); }, [load]);

    const board = data && data.key === paramsKey ? data.data : null;
    const locations = board?.locations || [];
    const items = (board?.items || []).filter((i) => !i.excluded);
    const cells = board?.cells || {};
    const flagsN = board?.flags_n ?? Object.values(cells).reduce(
        (n, byItem) => n + Object.values(byItem || {}).filter((c) => c && c.flag).length, 0);
    const msg = error ? dkMessage(error, lang) : null;
    const exportMonth = rangeMode === "month" ? month : (isIsoDate(from) ? from.slice(0, 7) : thisMonth);

    const openMonth = (locId) => {
        if (!canOpenMonth) return;
        const next = new URLSearchParams(searchParams);
        next.set("section", "dk");
        next.set("tab", "month");
        next.set("loc", locId);
        next.set("month", exportMonth);
        setSearchParams(next);
    };

    return (
        <div data-testid={V.view} className="space-y-3">
            <div className={`${cardClass} space-y-2 p-3`}>
                <div data-testid={V.range} data-mode={rangeMode} className="flex flex-wrap items-center gap-1.5">
                    {["month", "range"].map((m) => (
                        <button key={m} type="button" data-testid={V.rangeMode(m)} data-active={rangeMode === m ? "true" : "false"}
                                onClick={() => setRangeMode(m)} className={chipClass(rangeMode === m)}>
                            {t(m === "month" ? "dk_range_month" : "dk_range_custom")}
                        </button>
                    ))}
                    <div className="ms-auto">
                        <ExportButton month={exportMonth} testId={V.exportBtn} />
                    </div>
                </div>
                {rangeMode === "month" ? (
                    <MonthPicker months={months} value={month} onChange={setMonth} className="sm:w-56" />
                ) : (
                    <div className="grid grid-cols-2 gap-2 sm:max-w-md">
                        <label className="min-w-0 space-y-1">
                            <span className="block text-[11px] font-bold text-[color:var(--ink-muted)]">{t("dk_range_from")}</span>
                            <Input data-testid={V.dateFrom} type="date" dir="ltr" lang="ar" value={from} max={to || today || undefined}
                                   onChange={(e) => setFrom(e.target.value)} className="h-10 text-base" />
                        </label>
                        <label className="min-w-0 space-y-1">
                            <span className="block text-[11px] font-bold text-[color:var(--ink-muted)]">{t("dk_range_to")}</span>
                            <Input data-testid={V.dateTo} type="date" dir="ltr" lang="ar" value={to} min={from || undefined}
                                   max={today || undefined} onChange={(e) => setTo(e.target.value)} className="h-10 text-base" />
                        </label>
                        {!rangeOk ? <p className="col-span-2 text-[11px] font-semibold text-[color:var(--danger)]">{t("dk_range_invalid")}</p> : null}
                    </div>
                )}
                {board ? (
                    <p data-testid={V.flagsN} data-count={flagsN}
                       className={`flex items-center gap-1.5 text-xs font-semibold ${flagsN ? "text-[color:var(--danger-soft-fg)]" : "text-[color:var(--ink-muted)]"}`}>
                        <CalendarRange className="h-3.5 w-3.5 shrink-0" />
                        {flagsN ? fmt(t("dk_flags_n"), { n: fmtQuantity(flagsN, lang) }) : t("dk_no_flags")}
                    </p>
                ) : null}
            </div>

            {error && !board ? (msg ? <NoteBox>{msg}</NoteBox> : <OfflineBox onRetry={load} busy={loading} />) : null}

            {loading && !board ? <InlineLoader /> : board ? (
                !locations.length || !items.length ? (
                    <p data-testid={V.empty}
                       className="rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] py-10 text-center text-sm text-[color:var(--ink-muted)]">
                        {t("dk_variance_empty")}
                    </p>
                ) : (
                    <div className={`overflow-x-auto rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] transition-opacity duration-200 ${loading ? "opacity-60" : ""}`}>
                        <table data-testid={V.board} className="w-max min-w-full border-collapse text-sm">
                            <thead>
                                <tr className="bg-[color:var(--bg-surface-2)]">
                                    <th className="sticky z-20 min-w-[7rem] max-w-[9rem] bg-[color:var(--bg-surface-2)] px-3 py-2 text-start text-xs font-bold text-[color:var(--ink-muted)]"
                                        style={{ insetInlineStart: 0 }}>
                                        {t("dk_location")}
                                    </th>
                                    {items.map((it) => (
                                        <th key={it.id} className="min-w-[5.5rem] px-2 py-2 text-center text-[11px] font-bold text-[color:var(--ink-muted)]">
                                            <span className="block">{tn(it)}</span>
                                            <span className="block text-[10px] font-medium text-[color:var(--ink-faint)]">{unitOf(it, t)}</span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {locations.map((loc) => (
                                    <tr key={loc.id} className="border-t border-[color:var(--separator)]">
                                        <td className="sticky z-10 bg-[color:var(--bg-surface)] px-3 py-2 text-start" style={{ insetInlineStart: 0 }}>
                                            <span className="block truncate text-xs font-bold text-[color:var(--ink)]">{tn(loc)}</span>
                                        </td>
                                        {items.map((it) => {
                                            const c = (cells[loc.id] || {})[it.id];
                                            // no cell, or an item this location does not count (`excluded`): nothing to open
                                            if (!c || c.excluded) {
                                                return (
                                                    <td key={it.id} data-testid={V.cell(loc.id, it.id)} data-empty="true"
                                                        data-excluded={c?.excluded ? "true" : undefined}
                                                        className="px-2 py-2 text-center text-xs text-[color:var(--ink-faint)]">·</td>
                                                );
                                            }
                                            const pct = pctOf(c.unexplained, c.theoretical, c.pct);
                                            const has = c.unexplained !== null && c.unexplained !== undefined;
                                            return (
                                                <td key={it.id} data-testid={V.cell(loc.id, it.id)} data-flag={c.flag ? "true" : "false"}
                                                    data-value={has ? c.unexplained : ""}
                                                    className={`px-1 py-1 text-center ${c.flag ? "bg-[color:var(--danger-soft)]" : ""}`}>
                                                    <button type="button" onClick={() => openMonth(loc.id)} disabled={!canOpenMonth}
                                                            aria-label={`${tn(loc)} — ${tn(it)}`}
                                                            className="w-full rounded px-1 py-1 text-center enabled:hover:bg-[color:var(--brand-orange-soft)]">
                                                        <span className={`flex items-center justify-center gap-0.5 text-xs font-extrabold tabular-nums ${
                                                            c.flag ? "text-[color:var(--danger-soft-fg)]" : has ? "text-[color:var(--ink)]" : "text-[color:var(--ink-faint)]"
                                                        }`}>
                                                            {c.flag ? <AlertTriangle className="h-3 w-3 shrink-0" /> : null}
                                                            {has ? <bdi dir="ltr">{fmtSigned(c.unexplained, lang)}</bdi> : "—"}
                                                        </span>
                                                        {pct !== null ? (
                                                            <span className="block text-[10px] font-semibold tabular-nums text-[color:var(--ink-muted)]">
                                                                <bdi dir="ltr">{fmtPct(pct, lang)}</bdi>
                                                            </span>
                                                        ) : null}
                                                        {c.days_missing ? (
                                                            <span className="block text-[9px] font-semibold text-[color:var(--warning-soft-fg)]">
                                                                {fmt(t("dk_days_missing"), { n: fmtQuantity(c.days_missing, lang) })}
                                                            </span>
                                                        ) : null}
                                                    </button>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            ) : null}

            {board ? <p className="text-[11px] text-[color:var(--ink-faint)]">{t("dk_variance_legend")}</p> : null}
        </div>
    );
}
